import { FC, useState, useCallback, useMemo, ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import BasicModal from "../BasicModal";
import { normolizeCurrenciesDecimals } from "helpers/funcs";
import { purchasePhazeCard, getUsersPhazeProducts } from "api/phaze";
import { orderCard, getUsersCardsList } from "api/cardProduct";
import ConfirmPurchaseStep from "./ConfirmPurchaseStep";
import SuccessPurchaseStep from "./SuccessPurchaseStep";
import { GlobalStore } from "stores";
import { virtualCardDesigns, extraVirtualCardDesigns } from "helpers/consts";
import type { OrderCardReq } from "api/types/cardProduct";
import type { PurchaseData } from "api/types/phaze";
import type {
  AvailableTradeOptions,
  CurrencyInterface,
  PhazeProductInterface,
  ModalStepsType,
  CardTemplate,
  CardType,
  LimitsValueInterface,
} from "helpers/types";
import routesPaths from "routes/routesPaths";

interface CryptoProps {
  isCrypto: true;
  phoneNumber: string;
  embossName: string;
}

interface NonCryptoProps {
  isCrypto: false;
  phoneNumber?: string;
  embossName?: string;
}
interface CardPurchaseModalProps {
  isOpen: boolean;
  product?: PhazeProductInterface;
  cryptoProduct?: CardTemplate | null;
  productDescription?: string;
  productHowToUse?: string;
  productRermsAndConditions?: string;
  onClose: () => void;
  currencyValue: string;
  currency: AvailableTradeOptions | null;
  currencies: CurrencyInterface[] | null;
  designCode?: string;
  isCrypto: boolean;
  chosenCardType?: CardType | "";
  deliveryMethodId?: number;
  deliveryAddressId?: number;
  TransactionInfos: ReactNode;
  topUpFees?: string;
  withdrawFees?: string;
  giftCode?: string;
  limitsInfo?: LimitsValueInterface | null;
  chosenDeliveryAddressName?: string;
  cardWithDiscountGift?: boolean;
  cardIsFree?: boolean;
}

const CardPurchaseModal: FC<
  CardPurchaseModalProps & (CryptoProps | NonCryptoProps)
> = ({
  isOpen,
  product,
  productDescription,
  productHowToUse,
  productRermsAndConditions,
  onClose,
  currencyValue,
  currency,
  currencies,
  isCrypto,
  cryptoProduct,
  deliveryAddressId,
  chosenCardType,
  deliveryMethodId,
  designCode,
  TransactionInfos,
  topUpFees,
  withdrawFees,
  limitsInfo,
  phoneNumber,
  embossName,
  giftCode,
  chosenDeliveryAddressName,
  cardWithDiscountGift,
  cardIsFree,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [step, setStep] = useState<ModalStepsType>("confirm");
  const [orderedCardId, setOrderedCardId] = useState<number | null>(null);

  const onSubmit = () => {
    if (!currency) {
      return;
    }

    if (isCrypto && cryptoProduct && designCode) {
      if (cryptoProduct.isPromoCodePaymentEnabled) {
        setIsLoading(true);

        const purchaseData: OrderCardReq = {
          currencyName: currency || "EUR",
          cardProductId: cryptoProduct.id,
          designCode: designCode,
          cardType: "VIRTUAL",
          giftCode,
          phoneNumber: phoneNumber,
          embossName,
        };

        if (cardWithDiscountGift) {
          purchaseData.currencyName = currency;
        }

        if (
          chosenCardType === "PHYSICAL" &&
          typeof deliveryMethodId === "number" &&
          deliveryAddressId
        ) {
          purchaseData.deliveryMethodId = deliveryMethodId;
          purchaseData.deliveryAddressId = deliveryAddressId;
        }

        orderCard(purchaseData)
          .then(({ data }) => {
            setOrderedCardId(data.id);
            getUsersCardsList();
            setStep("success");
          })
          .catch((err) => {
            if (err?.response?.data?.message) {
              GlobalStore.setError(err.response.data.message);
            }
          })
          .finally(() => setIsLoading(false));
        return;
      }
      if (
        chosenCardType === "PHYSICAL" &&
        !deliveryAddressId &&
        (cryptoProduct.minimalUserLevel === "MIDDLE" ||
          cryptoProduct.minimalUserLevel === "SENIOR")
      ) {
        GlobalStore.setError(
          "No users shipping address found. Please contact support"
        );
        return;
      }

      setIsLoading(true);

      const purchaseData: OrderCardReq = {
        currencyName: currency,
        cardProductId: cryptoProduct.id,
        designCode: designCode,
        cardType: chosenCardType || "VIRTUAL",
        phoneNumber: phoneNumber,
        embossName,
      };

      if (
        chosenCardType === "PHYSICAL" &&
        typeof deliveryMethodId === "number" &&
        deliveryAddressId
      ) {
        purchaseData.deliveryMethodId = deliveryMethodId;
        purchaseData.deliveryAddressId = deliveryAddressId;
      }

      orderCard(purchaseData)
        .then(({ data }) => {
          setOrderedCardId(data.id);
          getUsersCardsList();
          setStep("success");
        })
        .catch((err) => {
          if (err?.response?.data?.message) {
            GlobalStore.setError(err.response.data.message);
          }
        })
        .finally(() => setIsLoading(false));
      return;
    }

    if (!isCrypto && product) {
      setIsLoading(true);

      const purchaseData: PurchaseData = {
        currencyName: currency,
        productId: product.productId,
        price: Number(
          normolizeCurrenciesDecimals(
            currencyValue,
            currency || "USD",
            currencies
          )
        ),
      };
      purchasePhazeCard(purchaseData)
        .then(() => {
          getUsersPhazeProducts();
          setStep("success");
        })
        .catch((err) => {
          if (err?.response?.data?.message) {
            GlobalStore.setError(err.response.data.message);
          }
        })
        .finally(() => setIsLoading(false));
    }
  };

  const closeModal = useCallback(() => {
    if (isLoading) {
      return;
    }
    if (step === "success") {
      if (isCrypto && orderedCardId) {
        navigate(`${routesPaths.usersCryptoCard}${orderedCardId}`);
      }
      if (!isCrypto && orderedCardId) {
        navigate(`${routesPaths.usersGiftCard}${orderedCardId}`);
      }
    }
    onClose();
  }, [step, isLoading, onClose, orderedCardId, isCrypto, navigate]);

  const cardImage = useMemo<string>(() => {
    if (isCrypto && cryptoProduct) {
      return cryptoProduct.designCodes[0] === "CA02"
        ? extraVirtualCardDesigns[cryptoProduct.currencyName]?.image || ""
        : virtualCardDesigns[cryptoProduct.designCodes[0]]?.image || "";
    }

    if (!isCrypto && product) {
      return product.productImage;
    }

    return "";
  }, [isCrypto, cryptoProduct, product]);

  const brandName = useMemo<string>(() => {
    if (isCrypto && cryptoProduct) {
      return cryptoProduct.cardBrand;
    }

    if (!isCrypto && product) {
      return product.brandName;
    }

    return "";
  }, [isCrypto, cryptoProduct, product]);

  const chosenCurrency = useMemo<string>(() => {
    if (isCrypto && cryptoProduct) {
      return cryptoProduct.currencyName;
    }

    if (!isCrypto && product) {
      return product.currency;
    }

    return "";
  }, [isCrypto, cryptoProduct, product]);

  return (
    <BasicModal
      onExited={() => setOrderedCardId(null)}
      isOpen={isOpen}
      onClose={closeModal}
      title="Purchase card"
      showLoader={isLoading}
    >
      {step === "confirm" && (
        <ConfirmPurchaseStep
          cardImage={cardImage}
          brandName={brandName}
          chosenCurrency={chosenCurrency}
          currency={currency}
          currencyValue={currencyValue}
          onClose={closeModal}
          onSubmit={onSubmit}
          isCrypto={isCrypto}
          TransactionInfos={TransactionInfos}
          giftCode={giftCode}
          phoneNumber={phoneNumber}
          embossName={embossName}
          chosenDeliveryAddressName={chosenDeliveryAddressName}
          cardWithDiscountGift={cardWithDiscountGift}
          cardIsFree={cardIsFree}
        />
      )}

      {step === "success" && (
        <SuccessPurchaseStep
          cardImage={cardImage}
          brandName={brandName}
          productDescription={productDescription}
          productHowToUse={productHowToUse}
          productRermsAndConditions={productRermsAndConditions}
          onClose={closeModal}
          isCrypto={isCrypto}
          TransactionInfos={TransactionInfos}
          topUpFees={topUpFees}
          withdrawFees={withdrawFees}
          limitsInfo={limitsInfo}
          level={cryptoProduct?.level}
        />
      )}
    </BasicModal>
  );
};

export default observer(CardPurchaseModal);
